import React from 'react';
import { ClockIcon } from './Icons';

type DaySchedule = { open: string; close: string } | null;
type WeekSchedule = Partial<Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', DaySchedule>>;

interface WorkingHoursBadgeProps {
  workingHours?: WeekSchedule | { text?: string } | Record<string, unknown> | null;
  className?: string;
}

// JS getDay(): 0=Sun, 1=Mon, ..., 6=Sat
const JS_DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const DAY_SHORT_RU = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

function parseMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function isWeekSchedule(wh: unknown): wh is WeekSchedule {
  if (!wh || typeof wh !== 'object') return false;
  return ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].some((k) => k in (wh as object));
}

function getStatusFromWeek(wh: WeekSchedule): { text: string; color: 'green' | 'red' | 'gray' } | null {
  const now = new Date();
  const todayIdx = now.getDay(); // 0–6
  const nowMin = now.getHours() * 60 + now.getMinutes();

  // Lookup today's schedule
  const todayKey = JS_DAY_KEYS[todayIdx];
  const todaySchedule = wh[todayKey];

  if (todaySchedule !== undefined && todaySchedule !== null) {
    const openMin = parseMinutes(todaySchedule.open);
    const closeMin = parseMinutes(todaySchedule.close); // "24:00" → 1440

    // 24/7
    if (openMin === 0 && closeMin === 1440) {
      return { text: 'Круглосуточно', color: 'green' };
    }

    const isOvernight = closeMin <= openMin; // e.g. open=720, close=60 (1am)

    let isOpen: boolean;
    if (isOvernight) {
      isOpen = nowMin >= openMin || nowMin < closeMin;
    } else {
      isOpen = nowMin >= openMin && nowMin < closeMin;
    }

    if (isOpen) {
      const closeLabel = todaySchedule.close === '24:00' ? '0:00' : todaySchedule.close;
      return { text: `Открыто до ${closeLabel}`, color: 'green' };
    }

    // Not open — check if opens later today
    if (nowMin < openMin) {
      return { text: `Закрыто до ${todaySchedule.open}`, color: 'red' };
    }
    // Past closing — fall through to find next open day
  }

  // Find next open day (starting from tomorrow)
  for (let delta = 1; delta <= 7; delta++) {
    const nextIdx = (todayIdx + delta) % 7;
    const nextKey = JS_DAY_KEYS[nextIdx];
    const nextSchedule = wh[nextKey];
    if (nextSchedule) {
      if (delta === 1) {
        // Tomorrow — just show time
        return { text: `Закрыто до ${nextSchedule.open}`, color: 'red' };
      }
      // 2+ days — show day
      return { text: `Закрыто до ${DAY_SHORT_RU[nextIdx]} ${nextSchedule.open}`, color: 'red' };
    }
  }

  return null;
}

const WorkingHoursBadge: React.FC<WorkingHoursBadgeProps> = ({ workingHours, className = '' }) => {
  if (!workingHours) return null;

  // New format: per-day schedule
  if (isWeekSchedule(workingHours)) {
    const status = getStatusFromWeek(workingHours as WeekSchedule);
    if (!status) return null;

    const colorClass =
      status.color === 'green' ? 'text-green-600' :
      status.color === 'red' ? 'text-red-500' :
      'text-gray-400';

    return (
      <span className={`inline-flex items-center gap-1 text-[11px] ${colorClass} ${className}`}>
        <ClockIcon className="w-3 h-3" />
        {status.text}
      </span>
    );
  }

  // Legacy format: { text: "HH:MM-HH:MM" }
  const legacy = workingHours as { text?: string };
  if (!legacy.text) return null;

  const text = legacy.text.trim();
  if (!text) return null;

  if (text === '00:00-24:00' || text.toLowerCase().includes('круглосуточно')) {
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] text-green-600 ${className}`}>
        <ClockIcon className="w-3 h-3" />
        Круглосуточно
      </span>
    );
  }

  const match = text.match(/^(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})$/);
  if (!match) {
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] text-gray-400 ${className}`}>
        <ClockIcon className="w-3 h-3" />
        {text}
      </span>
    );
  }

  const [, openH, openM, closeH, closeM] = match;
  const openMinutes = parseInt(openH) * 60 + parseInt(openM);
  let closeMinutes = parseInt(closeH) * 60 + parseInt(closeM);
  if (closeMinutes === 0 && parseInt(closeH) === 0) closeMinutes = 24 * 60;
  if (parseInt(closeH) === 24) closeMinutes = 24 * 60;

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const isOpen = closeMinutes > openMinutes
    ? nowMinutes >= openMinutes && nowMinutes < closeMinutes
    : nowMinutes >= openMinutes || nowMinutes < closeMinutes;

  if (isOpen) {
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] text-green-600 ${className}`}>
        <ClockIcon className="w-3 h-3" />
        Открыто до {closeH}:{closeM}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 text-[11px] text-red-500 ${className}`}>
      <ClockIcon className="w-3 h-3" />
      Закрыто до {openH}:{openM}
    </span>
  );
};

export default WorkingHoursBadge;
